import React from 'react';
import { get } from 'lodash-es';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useCallback from '@/util/hook/useCallback';
import useDispatch from '@/util/hook/useDispatch';
import useEffect from '@/util/hook/useEffect';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import { selectLiffId } from '@/util/redux/auth';
import { handleParamVerifyAndCachePut } from '../../util/helper/paramVerifyAndCachePut';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { useScrollToInputField } from '../../util/hook/useScrollToInputField';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { useValidateCareOrderBabyForm } from '../../util/hook/useValidateCareOrderBabyForm';
import { careOrderBabyFormUpdate, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectHospitalList } from '../../redux/module/remoteConfig';
import View from './View';
import { APP } from '../../config/app';
import { CareOrderBabyFormForCachePutDConverter } from '../../util/dataConverter/CareOrderBabyFormForCachePutDConverter';
import { CareOrderBabyFormForParamVerifyDConverter } from '../../util/dataConverter/CareOrderBabyFormForParamVerifyDConverter';
const getHospitalAddress = ({ selectedHospital, hospitalList, }) => {
    if (!selectedHospital || !hospitalList)
        return '';
    const hospitalData = hospitalList.find((hospital) => hospital.name === selectedHospital.name &&
        hospital.district === selectedHospital.district &&
        hospital.city === selectedHospital.city);
    const hospitalAddress = get(hospitalData, 'address', '');
    return hospitalAddress;
};
const useGoBackToUnfinishedPage = ({ selectedHospital, navigate, }) => {
    useEffect(() => {
        const hospitalName = get(selectedHospital, 'name', '');
        const hospitalCity = get(selectedHospital, 'city', '');
        const hospitalDistrict = get(selectedHospital, 'district', '');
        const isUnfinished = !hospitalName || !hospitalCity || !hospitalDistrict;
        if (!isUnfinished)
            return;
        navigate(APP.ROUTES.HOSPITAL);
    }, []);
};
export default function Controller() {
    const { isLoading, setLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const { getIsFieldError, setErrorTypes } = useValidateCareOrderBabyForm({});
    const { scrollToInputField, careOrderBabyFormInputFieldRefMap } = useScrollToInputField();
    const _id = useSelector(selectLiffId);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const selectedHospital = careOrderBabyForm.hospitalObj;
    const hospitalList = useSelector(selectHospitalList);
    const { navigate, navigateError } = useRouter();
    const dispatch = useDispatch();
    useTrackBrowserHistory();
    useGoBackToUnfinishedPage({ selectedHospital, navigate });
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useGetCareClientVisitorProfile({ setApiProcessingPool, setApiDonePool });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    // event handlers
    const handleBackButtonClick = useCallback(() => {
        dispatch(careOrderBabyFormUpdate({
            hospitalObj: null,
        }));
        navigate(APP.ROUTES.HOSPITAL);
    }, []);
    const handleForwardButtonClick = async () => {
        const { data: paramVerifyParams } = new CareOrderBabyFormForParamVerifyDConverter({
            hospitalObj: selectedHospital,
        });
        const { data: careOrderBabyFormForCachePut } = new CareOrderBabyFormForCachePutDConverter({
            careOrderBabyForm,
        });
        const { isSuccess } = await handleParamVerifyAndCachePut({
            _id,
            params: { _id, ...paramVerifyParams },
            careOrderBabyFormForCachePut,
            careOrderBabyFormInputFieldRefMap,
            setLoading,
            setErrorTypes,
            scrollToInputField,
            navigateError,
        });
        if (isSuccess) {
            navigate(APP.ROUTES.PATIENT_INFO);
        }
    };
    // parsed data
    const hospitalAddress = useMemo(() => getHospitalAddress({ selectedHospital, hospitalList }), [selectedHospital, hospitalList]);
    const hospitalName = get(selectedHospital, 'name', '');
    const isHospitalInvalid = getIsFieldError('hospitalObj');
    return (<View 
    // data
    isLoading={isLoading} hospitalName={hospitalName} hospitalAddress={hospitalAddress} isHospitalInvalid={isHospitalInvalid} 
    // func
    handleBackButtonClick={handleBackButtonClick} handleForwardButtonClick={handleForwardButtonClick}/>);
}
